import { useWeb3React } from '@web3-react/core';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Detail from '../../../../components/Detail';
import appContext from '../../../../context/app/appContext';
import useCountdown from '../../../../hooks/useCountdown';
import useClaimableStakingRewards from '../../../../hooks/web3/vault/useClaimableStakingRewards';
import useTotalAccumulatedVoSpool from '../../../../hooks/web3/vault/useTotalAccumulatedVoSpool';
import useUserAccumulatedVoSpool from '../../../../hooks/web3/vault/useUserAccumulatedVoSpool';
import useUserStakedSpool from '../../../../hooks/web3/vault/useUserStakedSpool';

const TRANCHE_TIME = 604800; //1week in seconds
const firstTrancheStartTime = 1648962000;

const VoSpoolEpoch = () => {
    const { account } = useWeb3React();
    const { sdk } = useContext(appContext);

    const { updateUserStakedSpool } = useUserStakedSpool();
    const { updateClaimableStakingRewards } = useClaimableStakingRewards();
    const { updateUserAccumulatedVoSpool } = useUserAccumulatedVoSpool();
    const { updateTotalAccumulatedVoSpool } = useTotalAccumulatedVoSpool();

    const [updated, setUpdated] = useState(false);

    const trancheIndex = useMemo(
        () => Math.floor((moment().unix() - firstTrancheStartTime) / TRANCHE_TIME + 1),
        []
    );

    const nextTrancheEndTime = useMemo(
        () => firstTrancheStartTime + trancheIndex * TRANCHE_TIME,
        [trancheIndex]
    );

    const timeRemaining = useCountdown(nextTrancheEndTime);

    const updateAll = useCallback(() => {
        updateUserStakedSpool();
        updateClaimableStakingRewards();
        updateUserAccumulatedVoSpool();
        updateTotalAccumulatedVoSpool();
    }, [
        updateUserStakedSpool,
        updateClaimableStakingRewards,
        updateUserAccumulatedVoSpool,
        updateTotalAccumulatedVoSpool,
    ]);

    useEffect(() => {
        if (
            !sdk ||
            !account ||
            updated ||
            moment().isBefore(moment.unix(nextTrancheEndTime))
        )
            return;

        updateAll();

        setUpdated(true);
    }, [sdk, account, timeRemaining, nextTrancheEndTime, updated, setUpdated, updateAll]);

    return (
        <Detail
            title='Next voSPOOL Epoch Begins in:'
            value={
                <div className='countdown-field-container'>
                    <CountdownField value={timeRemaining.days} title='days' />
                    <CountdownField value={timeRemaining.hours} title='hours' />
                    <CountdownField value={timeRemaining.mins} title='mins' />
                    <CountdownField value={timeRemaining.secs} title='secs' />
                </div>
            }
        />
    );
};

export default VoSpoolEpoch;

type Props = {
    value: string;
    title: string;
};

const CountdownField: React.FC<Props> = ({ value, title }) => (
    <div className='countdown-field'>
        <div className='value'>{value || 'Value'}</div>
        <div className='title'>{title || 'Title'}</div>
    </div>
);
